import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchDahliaBulbs, fetchPageBySlug } from '../lib/cms';

export default function BestilKnolde() {
  const [pageData, setPageData] = useState(null);
  const [bulbs, setBulbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderError, setOrderError] = useState(null);

  // Formular-felter
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    delivery_method: 'Afhentning', // Afhentning eller Forsendelse
    address: '',
    description: '',
    website_url: '', // Honeypot felt
  });

  useEffect(() => {
    async function loadBulbs() {
      try {
        const [data, page] = await Promise.all([
          fetchDahliaBulbs(),
          fetchPageBySlug('bestil-knolde').catch(() => null)
        ]);
        if (page) {
          setPageData(page);
        }
        // Filtrer kun knolde der er til salg (hvis til_salg eller for_sale er eksplicit false, skjules de)
        const activeBulbs = (data || []).filter((b) => (b.til_salg ?? b.for_sale) !== false);
        // Sorter alle knolde samlet i alfabetisk rækkefølge efter navn (A-Å)
        const sorted = activeBulbs.sort((a, b) => {
          const nameA = a.navn || a.name || '';
          const nameB = b.navn || b.name || '';
          return nameA.localeCompare(nameB, 'da', { sensitivity: 'base' });
        });
        setBulbs(sorted);
      } catch (err) {
        console.error('Fejl ved indlæsning af knolde:', err);
      } finally {
        setLoading(false);
      }
    }
    loadBulbs();
  }, []);

  // Kurv-styring
  const addToCart = (bulbId) => {
    setCart((prev) => ({
      ...prev,
      [bulbId]: (prev[bulbId] || 0) + 1,
    }));
  };

  const removeFromCart = (bulbId) => {
    setCart((prev) => {
      const updated = { ...prev };
      if (updated[bulbId] > 1) {
        updated[bulbId] -= 1;
      } else {
        delete updated[bulbId];
      }
      return updated;
    });
  };

  const clearCart = () => {
    setCart({});
  };

  // Beregn totaler
  const totalItems = Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = Object.entries(cart).reduce((sum, [bulbId, qty]) => {
    const bulb = bulbs.find((b) => b.id === bulbId);
    const price = bulb ? (bulb.pris ?? bulb.price ?? 0) : 0;
    return sum + price * qty;
  }, 0);

  // Håndter formular ændring
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  // Scroll til kurv/bestilling (særligt nyttigt på mobil)
  const scrollToOrder = () => {
    const el = document.getElementById('bestillingsformular');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        document.getElementById('name')?.focus({ preventScroll: true });
      }, 400);
    }
  };

  // Indsend bestilling
  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (totalItems === 0) {
      alert('Din bestillingsliste er tom. Tilføj venligst nogle knolde først.');
      return;
    }

    setSubmittingOrder(true);
    setOrderError(null);

    // Byg en pæn ordrebekræftelse / tekst til beskrivelsen
    const orderDetails = Object.entries(cart)
      .map(([bulbId, qty]) => {
        const bulb = bulbs.find((b) => b.id === bulbId);
        const name = bulb ? (bulb.navn || bulb.name || 'Ukendt') : 'Ukendt';
        const price = bulb ? (bulb.pris ?? bulb.price ?? 0) : 0;
        return `${name} (${qty} stk.) - ${price * qty} kr.`;
      })
      .join('\n');

    const fullDescription = `
BESTILLING AF DAHLIA KNOLDE:
----------------------------------
${orderDetails}

----------------------------------
Total antal: ${totalItems} stk.
Total pris: ${totalPrice} kr.
Leveringsmetode: ${customerInfo.delivery_method}
${customerInfo.delivery_method === 'Forsendelse' ? `Leveringsadresse: ${customerInfo.address}` : ''}

Kundens besked:
${customerInfo.description || 'Ingen besked'}
    `.trim();

    const payload = {
      name: customerInfo.name,
      phone: customerInfo.phone,
      email: customerInfo.email,
      description: fullDescription,
      website_url: customerInfo.website_url, // Honeypot
    };

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Der skete en fejl under afsendelsen af din bestilling. Prøv venligst igen.');
      }

      setOrderSubmitted(true);
      setCart({});
      setCustomerInfo({
        name: '',
        phone: '',
        email: '',
        delivery_method: 'Afhentning',
        address: '',
        description: '',
        website_url: '',
      });
    } catch (err) {
      setOrderError(err.message);
    } finally {
      setSubmittingOrder(false);
    }
  };

  return (
    <div className="pb-24">
      <Helmet>
        <title>{pageData?.meta_titel?.da || 'Bestil knolde | Dahliahjørnet - H.J.K. Multiservice'}</title>
        <meta
          name="description"
          content={pageData?.meta_beskrivelse?.da || 'Bestil dahlia knolde direkte online. Se vores udvalg af smukke dahlia-sorter til salg i denne sæson.'}
        />
      </Helmet>

      {/* Header Banner */}
      <div className="relative h-[40vh] min-h-[300px] bg-forest-950 flex items-center justify-center text-center px-6">
        <img
          src={pageData?.banner_billede?.[0] || 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/121-3592.jpg'}
          alt="Bestil dahlia knolde"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/50 to-transparent" />
        <div className="relative z-10 max-w-4xl">
          <span className="text-white uppercase tracking-[0.25em] font-bold text-xs sm:text-sm mb-3 block">
            {pageData?.underoverskrift?.da || 'Eksklusivt udvalg'}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
            {pageData?.titel?.da || 'Bestil dahlia knolde'}
          </h1>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-6 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Bulbs Catalog */}
          <div className="lg:col-span-8 flex flex-col gap-8">
            {/* Intro Text */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-cream-200">
              <h2 className="text-2xl font-serif font-bold text-forest-950 mb-4">
                Har du spørgsmål til vores salg af dahlia-knolde?
              </h2>
              <p className="text-charcoal-700 leading-relaxed mb-6">
                Vores kunder er naturligvis altid velkomne til at kontakte os, hvis de vil høre mere om vores udvalg, priser eller andre forhold. Derudover er du altid velkommen til at komme forbi og se vores udvalg af dahlia-knolde til salg.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-cream-50 p-4 rounded-2xl border border-cream-200">
                <span className="text-sm text-charcoal-700 font-medium">
                  Foretrækker du den traditionelle bestillingsliste som PDF?
                </span>
                <a
                  href="https://assets.auxodev.dk/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/bestilling.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-forest-700 hover:bg-forest-800 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-sm transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-file-pdf" />
                  Download PDF
                </a>
              </div>
            </div>

            {/* Knolde Header & Info */}
            <div className="flex items-center justify-between border-b border-cream-200 pb-4">
              <div>
                <h3 className="text-xl font-serif font-bold text-forest-950">
                  Dahlia-knolde til salg
                </h3>
                <p className="text-xs sm:text-sm text-charcoal-600 mt-0.5">
                  Alle sorter vises i alfabetisk rækkefølge fra A til Å.
                </p>
              </div>
              <span className="bg-cream-100 text-forest-950 text-xs font-bold px-3 py-1.5 rounded-full">
                {bulbs.length} sorter
              </span>
            </div>

            {/* Bulbs Grid */}
            {loading ? (
              <div className="text-center py-16">
                <i className="fa-solid fa-spinner animate-spin text-3xl text-dahlia-500 mb-3 block" />
                <p className="text-charcoal-600 font-medium">Henter dahlia-knolde...</p>
              </div>
            ) : bulbs.length === 0 ? (
              <div className="bg-white p-12 rounded-3xl text-center border border-cream-200">
                <i className="fa-solid fa-seedling text-4xl text-cream-400 mb-3 block" />
                <p className="text-charcoal-600 font-medium">Der er i øjeblikket ingen knolde til salg.</p>
                <p className="text-xs text-charcoal-500 mt-1">Nye knolde sættes løbende til salg i sæsonen.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {bulbs.map((bulb) => {
                  const bulbName = bulb.navn || bulb.name || '';
                  const bulbPrice = bulb.pris ?? bulb.price ?? 0;
                  const bulbImg = (bulb.billede && bulb.billede[0]) || (bulb.image && bulb.image[0]) || '';
                  const qtyInCart = cart[bulb.id] || 0;
                  const imageUrl = bulbImg
                    ? `https://assets.auxodev.dk/cdn-cgi/image/width=360,height=360,fit=cover,quality=80,format=auto/${bulbImg.replace('https://assets.auxodev.dk/', '')}`
                    : 'https://assets.auxodev.dk/cdn-cgi/image/width=360,height=360,fit=cover,quality=80,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/121-3592.jpg';

                  return (
                    <div
                      key={bulb.id}
                      className="bg-white rounded-3xl overflow-hidden shadow-xs border border-cream-200 flex flex-col justify-between group hover:shadow-md transition-all duration-300"
                    >
                      <div className="relative aspect-square bg-cream-100 overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={bulbName}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5 flex flex-col gap-4">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-serif font-bold text-forest-950 text-base leading-tight">
                            {bulbName}
                          </h4>
                          <span className="text-black font-bold text-base whitespace-nowrap">
                            {bulbPrice} kr.
                          </span>
                        </div>

                        {/* Cart Controls */}
                        {qtyInCart > 0 ? (
                          <div className="flex items-center justify-between bg-cream-100 rounded-xl p-1">
                            <button
                              type="button"
                              onClick={() => removeFromCart(bulb.id)}
                              className="w-8 h-8 rounded-lg bg-white hover:bg-cream-200 text-forest-950 flex items-center justify-center transition-colors cursor-pointer"
                              aria-label="Fjern en"
                            >
                              <i className="fa-solid fa-minus text-xs" />
                            </button>
                            <span className="font-bold text-forest-950 text-sm">{qtyInCart} stk.</span>
                            <button
                              type="button"
                              onClick={() => addToCart(bulb.id)}
                              className="w-8 h-8 rounded-lg bg-white hover:bg-cream-200 text-forest-950 flex items-center justify-center transition-colors cursor-pointer"
                              aria-label="Tilføj en mere"
                            >
                              <i className="fa-solid fa-plus text-xs" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => addToCart(bulb.id)}
                            className="w-full bg-forest-700 hover:bg-forest-800 text-white py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer"
                          >
                            <i className="fa-solid fa-cart-plus" />
                            Tilføj til bestilling
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column: Sticky Cart & Order Form */}
          <div
            id="bestillingsformular"
            className="lg:col-span-4 sticky top-36 min-[1200px]:top-56 flex flex-col gap-6 scroll-mt-36 min-[1200px]:scroll-mt-56"
          >
            <div className="bg-white p-5 sm:p-6 rounded-3xl shadow-sm border border-cream-200 flex flex-col max-h-[calc(100vh-10rem)] min-[1200px]:max-h-[calc(100vh-15rem)]">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-cream-200 shrink-0">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-cart-shopping text-xl text-forest-950" />
                  <h3 className="text-xl font-serif font-bold text-forest-950">Din bestilling</h3>
                </div>
                {totalItems > 0 && (
                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-xs text-charcoal-400 hover:text-rose-600 transition-colors cursor-pointer"
                  >
                    Tøm liste
                  </button>
                )}
              </div>

              {orderSubmitted ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl flex flex-col items-center text-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xl">
                    <i className="fa-solid fa-circle-check" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base">Mange tak for din bestilling!</h4>
                    <p className="text-xs mt-1">Vi har modtaget din bestillingsliste og vender tilbage til dig hurtigst muligt med bekræftelse og betalingsinfo.</p>
                  </div>
                </div>
              ) : totalItems === 0 ? (
                <div className="text-center py-8 text-charcoal-500">
                  <i className="fa-solid fa-cart-flatbed text-3xl text-cream-300 mb-2 block" />
                  <p className="text-sm">Din bestillingsliste er tom.</p>
                  <p className="text-xs mt-1">Klik på "Tilføj til bestilling" på de dahliaer, du ønsker at købe.</p>
                </div>
              ) : (
                <form id="order-actual-form" onSubmit={handleOrderSubmit} className="flex flex-col flex-1 min-h-0">
                  {/* Scrollable middle area for cart items + order fields */}
                  <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-4">
                    {/* Cart Items List */}
                    <div className="max-h-40 overflow-y-auto flex flex-col gap-2.5 pr-1 divide-y divide-cream-100">
                    {Object.entries(cart).map(([bulbId, qty]) => {
                      const bulb = bulbs.find((b) => b.id === bulbId);
                      if (!bulb) return null;
                      const bulbName = bulb.navn || bulb.name || 'Ukendt';
                      const bulbPrice = bulb.pris ?? bulb.price ?? 0;
                      return (
                        <div key={bulbId} className="flex items-center justify-between gap-4 text-sm border-b border-cream-100 pb-2">
                          <div className="flex flex-col">
                            <span className="font-semibold text-forest-950">{bulbName}</span>
                            <span className="text-xs text-charcoal-500">{bulbPrice} kr. pr. stk.</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-forest-950">{qty} stk.</span>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => removeFromCart(bulbId)}
                                className="w-6 h-6 rounded bg-cream-100 hover:bg-cream-200 text-forest-950 flex items-center justify-center text-xs cursor-pointer"
                              >
                                -
                              </button>
                              <button
                                type="button"
                                onClick={() => addToCart(bulbId)}
                                className="w-6 h-6 rounded bg-cream-100 hover:bg-cream-200 text-forest-950 flex items-center justify-center text-xs cursor-pointer"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals */}
                  <div className="bg-cream-50 p-3.5 rounded-2xl border border-cream-200 flex flex-col gap-1.5 shrink-0">
                    <div className="flex justify-between text-xs sm:text-sm text-charcoal-700">
                      <span>Antal dahliaer i alt:</span>
                      <span className="font-bold">{totalItems} stk.</span>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base text-forest-950 border-t border-cream-200 pt-1.5">
                      <span className="font-serif font-bold">Samlet pris:</span>
                      <span className="font-bold text-dahlia-600">{totalPrice} kr.</span>
                    </div>
                  </div>

                  {/* Order Form fields */}
                  <div className="flex flex-col gap-3.5 mt-1">
                    {/* Honeypot felt */}
                    <div className="sr-only absolute pointer-events-none" aria-hidden="true">
                      <input
                        type="text"
                        name="website_url"
                        value={customerInfo.website_url}
                        onChange={handleFormChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label htmlFor="name" className="text-xs font-bold text-charcoal-800">Navn *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={customerInfo.name}
                        onChange={handleFormChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:border-dahlia-500 outline-none bg-cream-50/30"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label htmlFor="phone" className="text-xs font-bold text-charcoal-800">Telefon *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={customerInfo.phone}
                          onChange={handleFormChange}
                          placeholder="Telefonnummer"
                          className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:border-dahlia-500 outline-none bg-cream-50/30"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label htmlFor="email" className="text-xs font-bold text-charcoal-800">E-mail *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={customerInfo.email}
                          onChange={handleFormChange}
                          placeholder="Din e-mail"
                          className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:border-dahlia-500 outline-none bg-cream-50/30"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-charcoal-800">Leveringsmetode *</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setCustomerInfo((prev) => ({ ...prev, delivery_method: 'Afhentning' }))}
                          className={`py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                            customerInfo.delivery_method === 'Afhentning'
                              ? 'bg-forest-700 text-white border-forest-700'
                              : 'bg-white text-charcoal-700 border-cream-300 hover:bg-cream-50'
                          }`}
                        >
                          Afhentning på gården
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomerInfo((prev) => ({ ...prev, delivery_method: 'Forsendelse' }))}
                          className={`py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                            customerInfo.delivery_method === 'Forsendelse'
                              ? 'bg-forest-700 text-white border-forest-700'
                              : 'bg-white text-charcoal-700 border-cream-300 hover:bg-cream-50'
                          }`}
                        >
                          Send med posten
                        </button>
                      </div>
                    </div>

                    {customerInfo.delivery_method === 'Forsendelse' && (
                      <div className="flex flex-col gap-1">
                        <label htmlFor="address" className="text-xs font-bold text-charcoal-800">Leveringsadresse *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          required={customerInfo.delivery_method === 'Forsendelse'}
                          value={customerInfo.address}
                          onChange={handleFormChange}
                          placeholder="Gade, postnr. og by"
                          className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:border-dahlia-500 outline-none bg-cream-50/30"
                        />
                      </div>
                    )}

                    <div className="flex flex-col gap-1">
                      <label htmlFor="description" className="text-xs font-bold text-charcoal-800">Besked / Bemærkninger</label>
                      <textarea
                        id="description"
                        name="description"
                        rows="3"
                        value={customerInfo.description}
                        onChange={handleFormChange}
                        placeholder="Evt. kommentarer til din bestilling..."
                        className="w-full px-3 py-2 rounded-xl border border-cream-300 text-sm focus:border-dahlia-500 outline-none bg-cream-50/30 resize-none"
                      />
                    </div>

                    {orderError && (
                      <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-xl text-xs flex items-center gap-2">
                        <i className="fa-solid fa-circle-exclamation text-sm" />
                        <span>{orderError}</span>
                      </div>
                    )}
                  </div>
                  </div>

                  {/* Fixed Bottom Submit Button inside sticky card */}
                  <div className="pt-3 mt-2 border-t border-cream-200 shrink-0 bg-white">
                    <button
                      type="submit"
                      disabled={submittingOrder}
                      className="w-full bg-dahlia-500 hover:bg-dahlia-600 disabled:bg-dahlia-300 text-white font-semibold py-3 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {submittingOrder ? (
                        <>
                          <i className="fa-solid fa-spinner animate-spin" />
                          Sender bestilling...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane" />
                          Send bestilling nu ({totalPrice} kr.)
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Bottom Cart Bar (vises på mobil/tablet når der er varer i kurven) */}
      {totalItems > 0 && !orderSubmitted && (
        <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40 bg-forest-950 text-white p-4 rounded-2xl shadow-xl border border-white/20 flex items-center justify-between gap-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex flex-col">
            <span className="text-xs text-cream-200">
              {totalItems} {totalItems === 1 ? 'dahlia' : 'dahliaer'} valgt
            </span>
            <span className="text-base font-bold font-serif">{totalPrice} kr. i alt</span>
          </div>
          <button
            type="button"
            onClick={scrollToOrder}
            className="bg-dahlia-500 hover:bg-dahlia-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <span>Gå til bestilling</span>
            <i className="fa-solid fa-arrow-down" />
          </button>
        </div>
      )}
    </div>
  );
}
